import { useCallback, useState } from 'react';
import { COMMENT_PREVIEW_MAX_CHARS } from './constants';

type CommentableItem = {
  id?: string;
  comment?: string | null;
};

type CommentPopoverState = {
  anchorEl: HTMLElement | null;
  itemId: string | null;
};

const INITIAL_STATE: CommentPopoverState = {
  anchorEl: null,
  itemId: null,
};

export const useCommentPopover = <T extends CommentableItem>(items: T[]) => {
  const [commentPopover, setCommentPopover] =
    useState<CommentPopoverState>(INITIAL_STATE);

  const openComment = useCallback(
    (event: React.MouseEvent<HTMLElement>, itemId: string) => {
      setCommentPopover({ anchorEl: event.currentTarget, itemId });
    },
    []
  );

  const closeComment = useCallback(() => {
    setCommentPopover(INITIAL_STATE);
  }, []);

  const isOpen = Boolean(commentPopover.anchorEl);

  const currentComment =
    items.find((item) => item.id === commentPopover.itemId)?.comment ?? '';

  const getCommentPreview = useCallback(
    (text: string | null, maxLength = COMMENT_PREVIEW_MAX_CHARS) => {
      if (!text || text.length <= maxLength) return text || '';
      return text.slice(0, maxLength) + '...';
    },
    []
  );

  const getCommentTooltip = useCallback(
    (comment: string | undefined, isReadOnly: boolean) => {
      if (isReadOnly) {
        return comment ? getCommentPreview(comment) : 'Комментарий отсутствует';
      }
      return comment ? getCommentPreview(comment) : 'Добавить комментарий';
    },
    [getCommentPreview]
  );

  return {
    anchorEl: commentPopover.anchorEl,
    itemId: commentPopover.itemId,
    isOpen,
    currentComment,
    openComment,
    closeComment,
    getCommentTooltip,
  };
};
